/**
 * Chat image attachments — pasting (or dropping, or picking) a screenshot into the chat composer.
 *
 * The bytes ride inline with the message: web → passerelle → the member's local agent SDK. Nothing
 * is uploaded to the backend, unlike an image dropped into the DOCUMENT (lib/image-upload.ts), which
 * is a stored blob referenced by the part's markdown. An attachment is part of the prompt, so it
 * stays on the local generation side.
 *
 * Oversized images are downscaled here rather than rejected: a Windows screenshot is routinely
 * 2560 px / several MB, which is both over the model's per-image cap and a needless pile of tokens.
 */
import type { ChatImage } from './api'
import { fileToBase64 } from './files'

/** What every supported agent (and the Anthropic API) can read — mirrors the passerelle's own list. */
export const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'] as const
/** Per message — kept in step with the passerelle's MAX_IMAGES. */
export const MAX_CHAT_IMAGES = 5
/** Per image, after downscaling. Same cap the passerelle enforces (and for the same reason: a full
 *  batch, base64-inflated, has to fit its 25 MB body limit). */
export const MAX_IMAGE_BYTES = 3.5 * 1024 * 1024
/** Longest edge worth sending: models downsample past ~1568 px anyway. */
export const MAX_IMAGE_EDGE = 1568

export type RejectReason = 'type' | 'size' | 'count'
export interface RejectedImage {
  name: string
  reason: RejectReason
}

/** The image files out of a paste's `clipboardData.files` or a drop's `dataTransfer.files`. */
export function imageFilesFrom(list: FileList | File[] | null | undefined): File[] {
  return Array.from(list ?? []).filter((f) => f.type.startsWith('image/'))
}

const isAccepted = (type: string): boolean => (ACCEPTED_IMAGE_TYPES as readonly string[]).includes(type)

/** Split a `data:<mime>;base64,<data>` URL — what canvas.toDataURL hands back. */
function parseDataUrl(url: string): { contentType: string; dataBase64: string } {
  const comma = url.indexOf(',')
  const header = url.slice(5, url.indexOf(';'))
  return { contentType: header || 'image/png', dataBase64: comma >= 0 ? url.slice(comma + 1) : url }
}

/** Canvas cannot re-encode a GIF; a scaled one loses its animation regardless, so PNG it is. */
const reencodeAs = (type: string): string => (type === 'image/gif' ? 'image/png' : type)

/**
 * Re-encode a too-large image down to MAX_IMAGE_EDGE. Returns null when nothing needs doing — and
 * also when the browser lacks the APIs (jsdom in tests), in which case the original bytes are used
 * and the size guard below still applies.
 */
async function downscale(file: File): Promise<ChatImage | null> {
  const oversized = file.size > MAX_IMAGE_BYTES
  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') return null
  let bitmap: ImageBitmap
  try {
    bitmap = await createImageBitmap(file)
  } catch {
    return null // undecodable here — let the raw bytes take their chances with the agent
  }
  const scale = Math.min(1, MAX_IMAGE_EDGE / Math.max(bitmap.width, bitmap.height))
  if (scale >= 1 && !oversized) {
    bitmap.close()
    return null
  }
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(bitmap.width * scale))
  canvas.height = Math.max(1, Math.round(bitmap.height * scale))
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    bitmap.close()
    return null
  }
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
  bitmap.close()
  // The browser falls back to PNG for a format it cannot encode, so read the type back off the
  // data URL rather than assuming the one we asked for.
  const { contentType, dataBase64 } = parseDataUrl(canvas.toDataURL(reencodeAs(file.type), 0.9))
  return { name: file.name, contentType, dataBase64 }
}

/** Read a file as-is (no re-encoding), keeping its own type. */
async function asIs(file: File): Promise<ChatImage> {
  return { name: file.name, contentType: file.type, dataBase64: await fileToBase64(file) }
}

/**
 * Turn dropped/pasted files into sendable attachments, applying every rule in one place:
 * accepted formats, the per-message count (counting what is already attached), and the per-image
 * size cap (after a downscaling attempt). Rejections come back named, so the composer can say
 * which file it refused and why instead of silently swallowing it.
 */
export async function filesToChatImages(
  files: File[],
  alreadyAttached = 0,
): Promise<{ images: ChatImage[]; rejected: RejectedImage[] }> {
  const images: ChatImage[] = []
  const rejected: RejectedImage[] = []
  let slots = Math.max(0, MAX_CHAT_IMAGES - alreadyAttached)
  for (const file of files) {
    const name = file.name || 'image'
    if (!isAccepted(file.type)) {
      rejected.push({ name, reason: 'type' })
      continue
    }
    if (slots === 0) {
      rejected.push({ name, reason: 'count' })
      continue
    }
    const image = (await downscale(file)) ?? (await asIs(file))
    // base64 inflates by 4/3 — compare the decoded length, which is what the agent's SDK sends.
    if ((image.dataBase64.length * 3) / 4 > MAX_IMAGE_BYTES) {
      rejected.push({ name, reason: 'size' })
      continue
    }
    images.push({ ...image, name })
    slots -= 1
  }
  return { images, rejected }
}

/** `src` for a thumbnail of an attachment — no object URL, so nothing to revoke. */
export const chatImageSrc = (img: ChatImage): string => `data:${img.contentType};base64,${img.dataBase64}`
